import {ID} from '@wandb/weave/core';

import {ViewObjSchema, ViewRef} from '../views/types';

/** The set of values for the type field of the view table  */
export type ViewTypes = ViewObjSchema['type'];
export type WorkspaceMaterialized = ViewObjSchema['whole'];
export type ViewID = ViewRef;

/** Defines the behavior of the workspace (eg saving behavior, readonly behavior) */
export const WorkspaceTypes = {
  user: 'user',
  default: 'default',
  named: 'named',
} as const;

export const NAME_PREFIX = 'nw-';
export const PUBLISHED_INDICATOR = 'p';
export const DRAFT_INDICATOR = 'd';

export const NW_PUBLISHED_USERNAME = 'published';
export const NW_PRIMARY_ID = 'primary';
export const getIdForUserPersonalNamedWorkspace = (username: string) =>
  `nwuser-${username}`;

/**
 * The related id is used to store the Id about the specifc sweep that's
 * being referenced. It can theoretically be extended to support groups/runs/etc
 * */
export type WsRelatedId = string | undefined;

export const getNamedWorkspaceId = (
  id: string,
  username: string,
  relatedId: WsRelatedId
): NamedWorkspaceId => {
  return {id, username, relatedId};
};

/** when you need a completely new FullNamedWorkspaceId, this will create a new unique id */
export const createNamedWorkspaceId = (
  username: string,
  relatedId: WsRelatedId
): NamedWorkspaceId => {
  return {id: ID(12), username, relatedId};
};

export const getNamedWorkspaceType = (nwId: NamedWorkspaceId) => {
  return nwId.username === NW_PUBLISHED_USERNAME ? 'published' : 'draft';
};

/**  This are the values needed to uniquely identify a particular named workspace instance */
export type NamedWorkspaceId = {
  id: string;
  username: string; // will be NW_PUBLISHED_USERNAME for the published view
  relatedId: WsRelatedId;
};

export type ReduxWorkspaceState = {
  ///// These attributes will all be populated after init

  /**
   * The viewList represents the current set of workspaces visible to the user in the
   * legacy workspace menu. It is pointer to a view list metadata query result in the views
  // reducer's lists attribute.
   * @deprecated for named workspaces
   */
  viewListID: string;
  /**
   * The type of the view represented in the current workspace
   */
  viewType: ViewTypes;
  /**
   * Each sweep has its own set of workspaces. This id is added to the view name column to uniquely identifies them.
   * Will only be populated for viewType 'sweep-view'
   */
  relatedId: WsRelatedId;
  // The spec to revert to if we need to clear
  defaultSpec: WorkspaceMaterialized;
  // TODO: test that when switching between projects, we properly change these
  // values so you're not writing to the old project/entity
  entityName: string;
  projectName: string;
  namedWorkspacesEnabled: boolean;

  // I'm keeping these deprecated fields around in case there is a migration
  // case. We should be be safe to remove these fields after dec 2023
  /** @deprecated The view name field for user workspaces */
  userWorkspaceName?: string;
  /** @deprecated The view name field for the default workspace */
  defaultWorkspaceName?: string;

  ///// These attributes change as the user switches between views being displayed
  /**
   * This attribute is changed when a new view is loaded, and is undefined during loading
   * Note that the id in this is the cid for the view.
   *  */
  viewRef: ViewID | undefined;

  /**
   * In named workspaces this holds the fnwid for the view being displayed
   */
  nwId: NamedWorkspaceId | undefined;

  /**
   * In named workspaces, this tracks the list of views that we are showing user
   * We no longer use the ViewListID since that adds extra complexity and
   * it's much easier to add new views this way.
   */
  namedWorkspaceViewCids?: string[];
};

// key is WorkspaceID
export type StateType = Record<string, ReduxWorkspaceState>;
